import { supabase } from '../lib/supabase.js';

export const profileDataService = {
  // Fetch complete profile data for a user
  async getCompleteProfile(userId) {
    try {
      const [academicResult, assessmentResult, familyResult, collegesResult] = await Promise.all([
        // Academic profile data
        supabase
          .from('student_academic_profiles')
          .select('*')
          .eq('user_id', userId)
          .single(),
        
        // Latest assessment response
        supabase
          .from('assessment_responses')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single(),
        
        // Family member data
        supabase
          .from('family_members')
          .select('*')
          .eq('id', userId)
          .single(),
        
        // College list data
        supabase
          .from('college_journey_colleges')
          .select(`
            *,
            global_college:global_colleges(*)
          `)
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
      ]);

      return {
        academic: academicResult.data || {},
        assessment: assessmentResult.data || {},
        family: familyResult.data || {},
        colleges: collegesResult.data || [],
        errors: {
          academic: academicResult.error,
          assessment: assessmentResult.error,
          family: familyResult.error,
          colleges: collegesResult.error
        }
      };
    } catch (error) {
      console.error('Error fetching complete profile:', error);
      throw error;
    }
  },

  // Calculate profile completeness
  calculateCompleteness(profile) {
    const sections = {
      basicInfo: {
        weight: 15,
        fields: ['name', 'grade_level', 'graduation_year', 'state_of_residency'],
        required: 4
      },
      schoolInfo: {
        weight: 10,
        fields: ['high_school_name', 'high_school_city', 'high_school_state', 'high_school_type', 'hs_competitiveness'],
        required: 4
      },
      academicPerformance: {
        weight: 20,
        fields: ['gpa_weighted', 'gpa_unweighted', 'gpa_scale', 'class_rank', 'class_size'],
        required: 3
      },
      testScores: {
        weight: 15,
        fields: ['sat_total', 'sat_math', 'sat_reading_writing', 'act_composite', 'national_merit_status'],
        required: 1
      },
      coursework: {
        weight: 10,
        fields: ['ap_courses_taken', 'honors_courses', 'dual_enrollment_credits', 'curriculum_rigor'],
        required: 2
      },
      activities: {
        weight: 15,
        fields: ['leadership_activities', 'extracurriculars', 'time_commitment', 'academic_awards', 'special_talents'],
        required: 2
      },
      goals: {
        weight: 10,
        fields: ['intended_major', 'career_goals', 'academic_interests', 'college_preferences', 'financial_aid_needed'],
        required: 2
      },
      personalBackground: {
        weight: 5,
        fields: ['first_generation_college', 'languages', 'geographic_region', 'special_circumstances'],
        required: 1
      }
    };

    let totalScore = 0;
    let maxScore = 0;
    const sectionScores = {};
    const missingFields = {
      critical: [],
      important: [],
      optional: []
    };

    Object.entries(sections).forEach(([sectionName, section]) => {
      let sectionComplete = 0;
      let sectionTotal = section.fields.length;
      
      section.fields.forEach((field, index) => {
        const value = this.getNestedValue(profile, field);
        
        // Check if we have a meaningful value (including numeric 0)
        const hasValue = value !== null && value !== undefined && 
                        (typeof value === 'number' || // Any number including 0
                         (typeof value === 'string' && value !== '') || // Non-empty string
                         typeof value === 'boolean' || // Boolean values
                         (Array.isArray(value) && value.length > 0) || // Non-empty array
                         (typeof value === 'object' && Object.keys(value).length > 0)); // Non-empty object
        
        if (hasValue) {
          sectionComplete++;
        } else if (index < section.required) {
          missingFields.critical.push({ section: sectionName, field });
        } else {
          missingFields.optional.push({ section: sectionName, field });
        }
      });

      const sectionPercentage = (sectionComplete / sectionTotal) * 100;
      sectionScores[sectionName] = {
        percentage: Math.round(sectionPercentage),
        complete: sectionComplete,
        total: sectionTotal
      };

      totalScore += (sectionPercentage / 100) * section.weight;
      maxScore += section.weight;
    });

    return {
      overall: Math.round(totalScore),
      sections: sectionScores,
      missingFields,
      completedFields: this.countCompletedFields(profile),
      totalFields: Object.values(sections).reduce((sum, s) => sum + s.fields.length, 0)
    };
  },

  // Helper to get nested values from profile object
  getNestedValue(obj, path) {
    const keys = path.split('.');
    let value = obj;
    
    for (const key of keys) {
      if (value?.academic && key in value.academic) {
        value = value.academic[key];
      } else if (value?.family && key in value.family) {
        value = value.family[key];
      } else if (value && key in value) {
        value = value[key];
      } else {
        return null;
      }
    }
    
    return value;
  },

  // Count completed fields (only those defined in sections)
  countCompletedFields(profile) {
    const sections = {
      basicInfo: ['name', 'grade_level', 'graduation_year', 'state_of_residency'],
      schoolInfo: ['high_school_name', 'high_school_city', 'high_school_state', 'high_school_type', 'hs_competitiveness'],
      academicPerformance: ['gpa_weighted', 'gpa_unweighted', 'gpa_scale', 'class_rank', 'class_size'],
      testScores: ['sat_total', 'sat_math', 'sat_reading_writing', 'act_composite', 'national_merit_status'],
      coursework: ['ap_courses_taken', 'honors_courses', 'dual_enrollment_credits', 'curriculum_rigor'],
      activities: ['leadership_activities', 'extracurriculars', 'time_commitment', 'academic_awards', 'special_talents'],
      goals: ['intended_major', 'career_goals', 'academic_interests', 'college_preferences', 'financial_aid_needed'],
      personalBackground: ['first_generation_college', 'languages', 'geographic_region', 'special_circumstances']
    };
    
    let count = 0;
    
    // Count only fields that are defined in our sections
    Object.values(sections).forEach(sectionFields => {
      sectionFields.forEach(field => {
        const value = this.getNestedValue(profile, field);
        // Count any meaningful value using same logic as calculateCompleteness
        const hasValue = value !== null && value !== undefined && 
                        (typeof value === 'number' || // Any number including 0
                        (typeof value == 'numeric' && value !== '') || // Any numeric string including 0
                        (typeof value == 'int4' && value !== '') || // Any integer string including 0
                         (typeof value === 'string' && value !== '' && value !== '[]' && value !== '{}') || // Non-empty string
                         typeof value === 'boolean' || // Boolean values
                         (Array.isArray(value) && value.length > 0) || // Non-empty array
                         (typeof value === 'object' && Object.keys(value).length > 0)); // Non-empty object
        if (hasValue) {
          count++;
        }
      });
    });
    
    return count;
  },

  // Check for data inconsistencies
  checkConsistencies(profile) {
    const inconsistencies = [];

    // Check GPA vs class rank consistency
    if (profile.academic?.gpa_unweighted && profile.academic?.class_rank) {
      const gpa = parseFloat(profile.academic.gpa_unweighted);
      const rank = parseInt(profile.academic.class_rank);
      const classSize = parseInt(profile.academic.class_size);
      
      if (gpa >= 3.95 && rank > 10) {
        inconsistencies.push({
          type: 'warning',
          field: 'class_rank',
          message: `Class rank of ${rank}/${classSize} seems inconsistent with ${gpa} GPA. Please verify.`
        });
      }
    }

    // Check SAT subscore total
    if (profile.academic?.sat_total && profile.academic?.sat_math && profile.academic?.sat_reading_writing) {
      const total = parseInt(profile.academic.sat_total);
      const math = parseInt(profile.academic.sat_math);
      const verbal = parseInt(profile.academic.sat_reading_writing);
      
      if (math + verbal !== total) {
        inconsistencies.push({
          type: 'error',
          field: 'sat_scores',
          message: `SAT subscores (${math} + ${verbal}) don't match total (${total}).`
        });
      }
    }

    return inconsistencies;
  },

  // Generate impact forecasts for missing data
  generateImpactForecasts(profile, missingFields, completenessData) {
    const forecasts = [];
    
    // Section weights matching the confidence calculation
    const sectionWeights = {
      assessment: 50,
      activities: 15,
      goals: 10,
      academicPerformance: 5,
      testScores: 5,
      coursework: 5,
      personalBackground: 5,
      basicInfo: 3,
      schoolInfo: 2
    };
    
    // Priority field suggestions for each section
    const sectionPriorityFields = {
      activities: {
        field: 'time_commitment',
        action: 'Add activity time commitments'
      },
      goals: {
        field: 'financial_aid_needed',
        action: 'Add financial planning context'
      },
      coursework: {
        field: 'curriculum_rigor',
        action: 'Add curriculum rigor level'
      },
      schoolInfo: {
        field: 'hs_competitiveness',
        action: 'Add school competitiveness context'
      },
      academicPerformance: {
        field: 'class_rank',
        action: 'Add class rank information'
      },
      testScores: {
        field: 'sat_total',
        action: 'Add standardized test scores'
      },
      personalBackground: {
        field: 'special_circumstances',
        action: 'Add personal context'
      }
    };
    
    // Check assessment first (biggest impact)
    if (!profile.assessment?.responses) {
      forecasts.push({
        action: 'Complete strategic assessment',
        impact: sectionWeights.assessment,
        field: 'assessment',
        section: 'assessment'
      });
    }
    
    // Calculate impact for each incomplete section
    if (completenessData?.sections) {
      Object.entries(completenessData.sections).forEach(([section, stats]) => {
        const weight = sectionWeights[section];
        if (weight && stats.percentage < 100) {
          // Calculate the actual impact of completing this section
          const remainingPercentage = (100 - stats.percentage) / 100;
          const potentialImpact = Math.round(weight * remainingPercentage * 10) / 10; // Round to 1 decimal
          
          if (potentialImpact > 0.5) { // Only show if impact is meaningful
            const priorityField = sectionPriorityFields[section];
            const missingCount = stats.total - stats.complete;
            
            forecasts.push({
              action: priorityField?.action || `Complete ${section} section (${missingCount} field${missingCount !== 1 ? 's' : ''})`,
              impact: potentialImpact,
              field: priorityField?.field || section,
              section: section
            });
          }
        }
      });
    }
    
    // Sort by impact
    return forecasts.sort((a, b) => b.impact - a.impact).slice(0, 5); // Top 5 items
  },

  // Get description for quick win fields
  getQuickWinDescription(field) {
    const descriptions = {
      curriculum_rigor: 'Add curriculum rigor level to strengthen your academic profile',
      hs_competitiveness: 'Add school competitiveness context to help colleges understand your achievements',
      test_optional: 'Indicate test-optional preference to align with college policies',
      time_commitment: 'Add time commitments for activities to quantify your leadership impact',
      financial_aid_needed: 'Add financial context to receive realistic college recommendations',
      special_circumstances: 'Add any special circumstances that provide important context',
      grade_trend: 'Add grade trend information to show academic trajectory',
      intended_major: 'Specify intended major to align with college programs',
      career_goals: 'Add career goals to demonstrate direction and purpose',
      learning_style: 'Describe learning style to find best-fit colleges'
    };
    return descriptions[field] || `Complete ${field} to improve your profile`;
  },

  // Update a specific field
  async updateField(userId, field, value, section = 'academic') {
    try {
      // All updates now go to student_academic_profiles table
      const table = 'student_academic_profiles';
      const idField = 'user_id';

      // First try to update the existing record
      const { data, error } = await supabase
        .from(table)
        .update({ [field]: value, updated_at: new Date().toISOString() })
        .eq(idField, userId)
        .select()
        .single();

      if (error) {
        // If record doesn't exist (PGRST116), create it first
        if (error.code === 'PGRST116') {
          console.log(`No student profile found for user ${userId}, creating one...`);

          // Create the profile record with the field value
          const { data: newData, error: createError } = await supabase
            .from(table)
            .insert({
              user_id: userId,
              [field]: value,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .select()
            .single();

          if (createError) {
            console.error('Error creating student profile:', createError);
            throw createError;
          }

          // Trigger profile task sync after creating new profile
          this.triggerProfileTaskSync(userId);

          return newData;
        } else {
          throw error;
        }
      }

      // Trigger profile task sync after successful update
      this.triggerProfileTaskSync(userId);

      return data;
    } catch (error) {
      console.error('Error updating field:', error);
      throw error;
    }
  },

  /**
   * Trigger profile task sync in the background
   * Called after any profile update to keep tasks in sync
   *
   * ENHANCED: Now uses TaskGenerationNotificationService to surface
   * sync results to the user (success or failure)
   */
  async triggerProfileTaskSync(userId) {
    try {
      // Import dynamically to avoid circular dependencies
      const ProfileTaskGenerationService = (await import('./ProfileTaskGenerationService')).default;
      const TaskGenerationNotificationService = (await import('./TaskGenerationNotificationService')).default;

      // Run sync in background (don't await to avoid blocking the update)
      ProfileTaskGenerationService.syncProfileTasksWithEpic(supabase, userId)
        .then(result => {
          console.log(`[Profile Sync] Tasks updated for user ${userId}:`, result);

          // Store notification for user to see
          if (result.success) {
            if (result.tasksCreated > 0 || result.tasksCompleted > 0) {
              // Only notify if something changed
              const notification = TaskGenerationNotificationService.createNotification(
                'success',
                `Profile tasks synced: ${result.tasksCreated} created, ${result.tasksCompleted} completed`,
                { syncResult: result, userId }
              );
              TaskGenerationNotificationService.storeNotification(userId, notification);
            }
          } else {
            // Notify user of sync failure
            const notification = TaskGenerationNotificationService.createNotification(
              'error',
              `Profile sync failed: ${result.error || 'Unknown error'}. Your tasks may be out of date.`,
              { syncResult: result, userId, errorCode: result.errorCode }
            );
            TaskGenerationNotificationService.storeNotification(userId, notification);
          }
        })
        .catch(error => {
          console.error(`[Profile Sync] Failed to sync tasks for user ${userId}:`, error);

          // Store error notification
          const TaskGenerationNotificationService = require('./TaskGenerationNotificationService').default;
          const notification = TaskGenerationNotificationService.createNotification(
            'error',
            `Profile sync crashed: ${error.message}. Please contact support if this persists.`,
            { error: error.message, userId }
          );
          TaskGenerationNotificationService.storeNotification(userId, notification);
        });
    } catch (error) {
      console.error('[Profile Sync] Error triggering sync:', error);
    }
  },

  // Export profile data in various formats
  exportProfile(profile, format = 'json') {
    switch (format) {
      case 'json':
        return JSON.stringify(profile, null, 2);
      
      case 'text':
        return this.formatAsText(profile);
      
      case 'csv':
        return this.formatAsCSV(profile);
      
      default:
        return profile;
    }
  },

  // Format profile as readable text
  formatAsText(profile) {
    let text = 'STUDENT PROFILE\n===============\n\n';
    
    // Basic Info
    text += 'BASIC INFORMATION\n';
    text += `-----------------\n`;
    text += `Name: ${profile.academic?.name || profile.family?.name || 'Not specified'}\n`;
    text += `Grade: ${profile.academic?.grade_level || profile.family?.grade || 'Not specified'}\n`;
    text += `Graduation Year: ${profile.academic?.graduation_year || 'Not specified'}\n\n`;
    
    // Academic Performance
    text += 'ACADEMIC PERFORMANCE\n';
    text += `-------------------\n`;
    text += `GPA (Weighted): ${profile.academic?.gpa_weighted || 'Not specified'}\n`;
    text += `GPA (Unweighted): ${profile.academic?.gpa_unweighted || 'Not specified'}\n`;
    text += `Class Rank: ${profile.academic?.class_rank || 'Not specified'}/${profile.academic?.class_size || 'Not specified'}\n\n`;
    
    // Test Scores
    text += 'TEST SCORES\n';
    text += `-----------\n`;
    text += `SAT Total: ${profile.academic?.sat_total || 'Not specified'}\n`;
    text += `ACT Composite: ${profile.academic?.act_composite || 'Not specified'}\n\n`;
    
    return text;
  },

  // Format profile as CSV
  formatAsCSV(profile) {
    const headers = ['Field', 'Value'];
    const rows = [];
    
    const addRow = (field, value) => {
      rows.push([field, value || 'Not specified']);
    };

    addRow('Name', profile.academic?.name || profile.family?.name);
    addRow('Grade', profile.academic?.grade_level || profile.family?.grade);
    addRow('GPA Weighted', profile.academic?.gpa_weighted);
    addRow('GPA Unweighted', profile.academic?.gpa_unweighted);
    addRow('SAT Total', profile.academic?.sat_total);
    addRow('ACT Composite', profile.academic?.act_composite);
    
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  }
};

export default profileDataService;